import { createSlice } from '@reduxjs/toolkit';
import { IAuthState, UserActionPayload } from './types';
import {
  baseRequestState,
  setFetchingError,
  setFetchingRequest,
  setFetchingSuccess,
} from '../helpers';
import {
  fetchLoginViaGoogle,
  fetchLoginViaLinkedIn,
  fetchLoginViaPassword,
  fetchRegisterAsJournalist,
  getProfile,
} from './asyncThunk';
import { LocalStorageApi } from '../../api/localStorage';
import { USER_STATUS } from '../../utils/types/user';
import { IUser } from '../../api/base/Auth/types';
import { SUCCESS_STATUS } from '../../utils/types/statuses';

const initialState: IAuthState = {
  user: null,
  requests: {
    loginViaPassword: {
      ...baseRequestState,
    },
    loginViaGoogle: {
      ...baseRequestState,
    },
    loginViaLinkedIn: {
      ...baseRequestState,
    },
    getProfile: {
      ...baseRequestState,
    },
    registerAsJournalist: {
      ...baseRequestState,
    },
  },
};

const getUpdatedUser = (user: IUser) => {
  return {
    ...user,
    status: user.date_deleting ? USER_STATUS.WILL_BE_DELETED : user.status,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: () => ({ ...initialState }),
    logout() {
      LocalStorageApi.removeAccessToken();
      LocalStorageApi.removeRegisterToken();
      return {
        ...initialState,
        requests: {
          ...initialState.requests,
          getProfile: { ...baseRequestState, status: SUCCESS_STATUS },
        },
      };
    },
    setUser(state, action: UserActionPayload) {
      state.user = getUpdatedUser(action.payload);
    },
    setUserMsStatus(state, action: any) {
      if (state.user) {
        state.user.twofa_status = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    const asyncActions = [
      [fetchLoginViaPassword, 'loginViaPassword'],
      [fetchLoginViaGoogle, 'loginViaGoogle'],
      [fetchLoginViaLinkedIn, 'loginViaLinkedIn'],
      [getProfile, 'getProfile'],
      [fetchRegisterAsJournalist, 'registerAsJournalist'],
    ] as const;

    asyncActions.forEach(([asyncThunk, requestStatus]) => {
      builder
        .addCase(asyncThunk.pending, (state) => {
          setFetchingRequest(state.requests[requestStatus]);
        })
        .addCase(asyncThunk.fulfilled, (state, action) => {
          setFetchingSuccess(state.requests[requestStatus]);
          state.user = getUpdatedUser(action.payload);
        })
        .addCase(asyncThunk.rejected, (state, { payload }) => {
          setFetchingError(state.requests[requestStatus], payload);
        });
    });
  },
});

export const { setUser, setUserMsStatus, logout } = authSlice.actions;
export const authReducer = authSlice.reducer;
